import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Button } from '@uhg-abyss/web/ui/Button';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { RenderHtml } from '@sde/widgets-core/src/common/RenderHtml';
import { useModal } from 'src/state/modal';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useUpdateOONConsentQuery } from '@sde/widgets-core/src/common/hooks/useUpdateOONConsentModalQuery';
import { event } from '@uhg-abyss/web/tools/event/event';

const StyledHeading = styled(Heading, {
  '&.abyss-heading-root': {
    fontFamily: 'UHCSerif,Georgia Bold,serif',
    fontWeight: '$semibold',
    fontSize: '2.027rem',
    lineHeight: '3rem',
  },
});

const StyledDiv = styled('div', {
  width: '100%',
  marginTop: '15px',
});

const FlexDiv = styled('div', {
  display: 'flex',
  justifyCenter: 'center',
  alignItems: 'center',
});

const StyledTextInput = styled(TextInput, {
  '&.abyss-text-input-label > span:nth-child(1)': {
    position: 'relative',
  },
  '&.abyss-text-input': {
    paddingLeft: '35px',
  },
});

const StyledModal = styled(Modal, {
  '&.abyss-modal-header-container': { marginTop: '$md' },
  '&.abyss-modal-overlay': {
    // adding one more 9 here to be layed on top of provider drawer
    zIndex: 9999999,
  },
});

export const validateEmailAddress = () => {
  return {
    validate: {
      required: (value) => {
        if (
          value === '' ||
          !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            value
          )
        ) {
          return 'Please enter a valid email address.';
        }
        return null;
      },
    },
  };
};

export const OONConsentModal = ({
  corporateProviderMpin,
  taxIdNumber,
  providerEmail,
}) => {
  const { getMessage, getAssetByKey } = usePPDLagoon();
  const { modals } = useModal.state();
  const { toggleModal } = useModal.actions();
  const form = useForm({
    defaultValues: {
      email: providerEmail,
    },
  });
  form.watch();
  const emailAddress = form.getValues('email');
  const { toast } = useToast();
  const { user } = useUser.state();
  const { isTechnicalUser } = user;
  const [firstRenderEmail, setFirstRenderEmail] = useState(false);

  const titleSubtext1Message = getMessage('oon-consent-modal-title-subtext1');
  const confirmationPopupMessage = getMessage(
    'oon-consent-modal-confirmation-popup-message'
  );
  const confirmationApiErrorPopupMessage = getMessage(
    'oon-consent-modal-confirmation-api-error-popup-message'
  );
  const paperlessImage = getAssetByKey('PAPERLESS_IMAGE');

  const styledEmailAddress = (
    <div>
      {confirmationPopupMessage}{' '}
      <span style={{ color: 'white', fontWeight: 'bold' }}>{emailAddress}</span>
      .
    </div>
  );

  const handleClose = () => {
    toggleModal({
      key: 'oon-consent-modal',
      isOpen: false,
    });
  };

  const handleNotNowButton = () => {
    toggleModal({
      key: 'oon-consent-modal',
      isOpen: false,
    });
    event('OON_CONSENT_MODAL_CLOSE', {
      modalName: 'Paperless Consent Modal',
    });
    event('OON_CONSENT_START', {
      jobName: 'Paperless Consent Opt-out',
    });
    toggleModal({
      key: 'oon-consent-optout-confirmation-modal',
      isOpen: true,
    });
  };

  const [, updatePopup] = useUpdateOONConsentQuery({
    onCompleted: () => {
      toast.show({
        title: 'Paperless delivery confirmed',
        message: styledEmailAddress,
        variant: 'success',
      });
    },
    onError: () => {
      toast.show({
        title: 'Unable to enroll in paperless delivery',
        message: confirmationApiErrorPopupMessage,
        variant: 'error',
      });
    },
  });

  const checkEmail = () => {
    if (!firstRenderEmail) {
      setFirstRenderEmail(true);
      event('OON_CONSENT_CLICK', {
        interactionName: 'Email address for eDelivery',
        interactionValue: 'Change Email Address',
        interactionContext: 'Paperless Consent Modal',
      });
    }
  };

  const onSubmit = () => {
    if (!isTechnicalUser) {
      updatePopup({
        variables: {
          corpOwnerId: corporateProviderMpin,
          taxIdNumber,
          providerEmail,
          convertToElectronic: true,
        },
      });
      event('OON_CONSENT_CLICK', {
        interactionName: 'Paperless consent',
        interactionValue: 'I consent',
        interactionContext: 'Paperless Consent Modal',
      });
      event('OON_CONSENT_MODAL_CLOSE', {
        modalName: 'Paperless Consent Modal',
      });
    }
    toggleModal({
      key: 'oon-consent-modal',
      isOpen: false,
    });
    return false;
  };

  useEffect(() => {
    form.setValue('email', providerEmail);
  }, [providerEmail]);

  return (
    <React.Fragment>
      <StyledModal
        id="oon-consent-modal"
        isOpen={modals['oon-consent-modal']}
        hideClose={!isTechnicalUser}
        onClose={handleClose}
        closeOnClickOutside={isTechnicalUser}
        shouldCloseOnEsc={isTechnicalUser}
      >
        <div>
          <img src={paperlessImage} alt="paperless_image" />
        </div>
        <Modal.Section>
          <StyledHeading offset={1} textAlign="left">
            Paperless consent
          </StyledHeading>
          <StyledDiv>
            <RenderHtml>{titleSubtext1Message}</RenderHtml>
          </StyledDiv>
        </Modal.Section>
        <Modal.Section>
          <FormProvider state={form} onSubmit={onSubmit}>
            <StyledTextInput
              label="Email address for eDelivery"
              placeholder="Enter email address"
              model="email"
              isClearable
              inputLeftElement={
                <IconSymbol icon="mail_outline" size="18px" color="$black" />
              }
              width="45%"
              onChange={() => checkEmail()}
              validators={validateEmailAddress()}
              onBlur={() => form.validate('email')}
            />
            <br />
            <Layout.Group alignLayout="right" alignItems="center" space={10}>
              <FlexDiv>
                <Button
                  type="submit"
                  isDisabled={isTechnicalUser}
                  variant="tertiary"
                  onClick={() => handleNotNowButton()}
                >
                  Opt-out
                </Button>
              </FlexDiv>
              <Button type="submit" isDisabled={isTechnicalUser}>
                I consent
              </Button>
            </Layout.Group>
          </FormProvider>
        </Modal.Section>
      </StyledModal>
    </React.Fragment>
  );
};

OONConsentModal.propTypes = {
  corporateProviderMpin: PropTypes.string.isRequired,
  taxIdNumber: PropTypes.string.isRequired,
  providerEmail: PropTypes.string.isRequired,
};
